import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Gauge } from 'lucide-react'
import type { KpiTrends } from '@/types/analytics'
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_TOOLTIP_PROPS,
} from '@/features/dashboard/components/chart-theme'
import { monthLabel } from './chart-utils'

interface AbsorptionChartProps {
  trends: KpiTrends | undefined
  isLoading: boolean
}

// Absorption rate by month: service + parts gross as a share of total
// overhead. 100% means fixed costs are covered.
export function AbsorptionChart({ trends, isLoading }: AbsorptionChartProps) {
  const chartData =
    trends?.absorption_pct.map((p) => ({ month: monthLabel(p.month), pct: p.pct })) ?? []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Gauge className="h-4 w-4" /> Absorption Rate
        </CardTitle>
        <CardDescription>
          Service + parts gross ÷ overhead — target ≥ 100%, red flag below 80%
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-[200px] w-full" />
        ) : chartData.length === 0 ? (
          <div className="text-muted-foreground flex h-[200px] items-center justify-center text-sm">
            No absorption data
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid {...CHART_GRID_PROPS} />
              <XAxis dataKey="month" tick={CHART_AXIS_TICK} axisLine={false} tickLine={false} />
              <YAxis
                tick={CHART_AXIS_TICK}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v: number) => `${v}%`}
              />
              <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(v) => `${Number(v).toFixed(1)}%`} />
              <ReferenceLine
                y={100}
                stroke="hsl(var(--success))"
                strokeDasharray="4 4"
                label={{
                  value: '100%',
                  position: 'insideTopRight',
                  fontSize: 10,
                  fill: 'hsl(var(--muted-foreground))',
                }}
              />
              <ReferenceLine y={80} stroke="hsl(var(--destructive))" strokeDasharray="4 4" />
              <Line
                type="monotone"
                dataKey="pct"
                name="Absorption %"
                stroke="hsl(var(--chart-2))"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  )
}
